import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { StatusCell } from '@/components/molecules/StatusCell';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { Account } from '@/types/contacts';
import { PaymentRailType } from '@/types/currency';
import { FiatAccountType } from '@/types/fiatAccountType';
import { Locale } from '@/types/locale';
import { simplifyTransactionStatus, Transaction } from '@/types/transaction';
import { transactionFromToAmount } from '@/utility/transactionFromToAmount';

interface Props {
  readonly transaction: Transaction;
  readonly field: keyof Transaction;
}

const emptyPaymentTypes = [FiatAccountType.unset, PaymentRailType.LocalExchange];

export const TransactionCellContent: React.FC<Props> = (props: Props): React.ReactElement => {
  const { transaction, field } = props;
  const locale = React.useContext<Locale>(LocaleContext);
  const tr = useTranslator();
  const dateFormatter = useDateFormatter('MMM d, yyyy');

  switch (field) {
    case 'date':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {dateFormatter.parseAndFormat(transaction.date)}
        </Typography>
      );
    case 'type':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {tr(transaction.type)}
        </Typography>
      );
    case 'paymentType':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {emptyPaymentTypes.includes(transaction.paymentType) ? '--' : tr(transaction.paymentType)}
        </Typography>
      );
    case 'fromAmount':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {transactionFromToAmount(locale, transaction.fromAmount, transaction.toAmount)}
        </Typography>
      );
    case 'fromAccount':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {transaction.fromAccount ? tr(accountToString(transaction.fromAccount)) : ''}
        </Typography>
      );
    case 'toAccount':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {transaction.toAccount ? tr(accountToString(transaction.toAccount)) : ''}
        </Typography>
      );
    case 'status':
      return (
        <Box sx={styles.cell}>
          <StatusCell
            status={simplifyTransactionStatus(transaction.status, transaction.completed)}
          />
        </Box>
      );
    default:
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {String(transaction[field as keyof Transaction])}
        </Typography>
      );
  }
};

const styles: Record<string, SystemStyleObject> = {
  cell: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

const accountToString = (account: Account): string => {
  return account.name;
};
